import React, { useEffect, useState } from "react";
import ColorButton from "../shared/colorButton";
import { PRODUCT_COLORS } from "../../utils/colors";
import { Link } from "gatsby";
import { Product, ProductVariation } from "../../utils/types";
import SizeButton from "../shared/sizeButton";
import AddToCartButton from "../shared/addToCartButton";
import { getVariationByColor } from "../../utils/utils";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { useProductColors } from "../../hooks/useProductColors";
import { useOptions } from "../../hooks/useOptions";

type ProductCardProps = {
  className?: string;
  showFull?: boolean;
  product: Product;
  mainImageLoading?: "eager" | "lazy";
};

export type ColorType = keyof typeof PRODUCT_COLORS;

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  showFull = false,
  className = "",
  mainImageLoading = "eager",
}) => {
  // showFull = true;
  if (!product?.product_variations) return;
  product.product_variations = product.product_variations.filter(item => item.status === 'publish');
  const defaultVariationId = product.defaultVariationID;
  const variationsAll = product.product_variations;

  const defaultColor = () => {
    const variation = variationsAll.find(variation => variation.id === defaultVariationId);
    if (variation && variation.attributes2 && variation.attributes2[0]) {
      const attribute = variation.attributes2[0].value;
      return attribute ? attribute : null;
    }

    if (variationsAll && variationsAll[0] && variationsAll[0].attributes2 && variationsAll[0].attributes2[0]) {
      return variationsAll[0].attributes2[0].value;
    }

    return null;
  }

  const defaultSize = () => {
    const variation = variationsAll.find(variation => variation.id === defaultVariationId);
    if (variation && variation.attributes2 && variation.attributes2[1] && variation.attributes2[1].value) {
      return variation.attributes2[1].value;
    }

    if (variationsAll[0] && variationsAll[0].attributes2 && variationsAll[0].attributes2[1] && variationsAll[0].attributes2[1].value) {
      return variationsAll[0].attributes2[1].value;
    }

    return null;
  }

  const getDefaultStockStatus = () => {
    const default_variation = product.product_variations.find(item => item.id === product.defaultVariationID);
    return default_variation?.stock_status ? default_variation.stock_status : product.stock_status;
  };
  const [stockStatus, setStockStatus] = useState(getDefaultStockStatus());

  const [color, setColor] = useState(defaultColor);
  const [size, setSize] = useState(defaultSize);
  const [variationId, setVariationId] = useState(defaultVariationId);
  const quantity = 1;

  const variationsByColor = getVariationByColor(product?.product_variations);

  const href = `/p/${product.slug}`;
  const options = useOptions();
  const currency_symbol = options.wpOptions.data.currency_symbol;

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  let currency = 'USD';
  if (process.env.GATSBY_PURCHASE_EVENT === 'true') {
    const options = useOptions();
    currency = options.wpOptions.data.currency;
  }

  const mainImageSrc = product.image ? product.image.src : "";
  const [currentImage, setCurrentImage] = useState<IGatsbyImageData | string>(product.image?.localFile?.childrenImageSharp[0]?.gatsbyImageData || mainImageSrc);

  useEffect(() => {
    if (color && variationsByColor[color]) {
      const firstVariation = variationsByColor[color][0];
      if (firstVariation && firstVariation.image) {
        const newImage = firstVariation.image.localFile?.childrenImageSharp[0]?.gatsbyImageData || firstVariation.image.src;
        setCurrentImage(newImage);
      }
    }
  }, [color]);

  return (
    <div
      id={`product-card-${product.id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`${className} group relative flex flex-col transition-all duration-300 lg:max-h-[440px] xl:max-h-[465px]
        ${showFull ? "" : isHovered ? "lg:bg-white" : ""}"}
        md:max-w-[332px] lg:max-w-[288px] xl:max-w-[299px]`}
    >
      <Link aria-label={product.name} to={href}>
        <div className="relative mb-[4px] h-[90vw] w-full overflow-hidden transition-all
        md:h-[332px] md:w-[332px]
        lg:h-[288px] lg:w-[288px]
        xl:h-[299px] xl:w-[299px]">
          <MainImage
            currentImage={currentImage}
            mainImageLoading={mainImageLoading}
            altText={product.image.alt}
          />
          <SecondaryImage product={product} />
        </div>
      </Link>
      <div className={`lg:mt-[-60px] ${isHovered ? '' : 'lg:overflow-hidden'}`}>
        <div
          className={`${className} group relative flex flex-col transition-all duration-200 z-10
            ${showFull ? "top-[0px]" : isHovered ? "lg:bg-white lg:translate-y-0 " : "lg:translate-y-[60px]"}
            w-full`}
        >
          <Link aria-label={product.name} to={href}>
            <p className="my-[12px] h-[48px] text-center font-verano text-base-16 text-dark-gray cursor-pointer">
              {product.name}
            </p>
          </Link>
          <div className="flex flex-col items-center justify-center">
            <div className="text-title-r font-semibold mb-[16px]  font-verano text-light-red sm:mb-[18px]">
              <span dangerouslySetInnerHTML={{ __html: currency_symbol }} /> {product.price || ""}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-[12px]">
              <ColorSelector
                setColor={setColor}
                activeColor={color}
                colors={variationsByColor}
                product={product}
                activeSize={size}
                setVariationId={setVariationId}
                setStockStatus={setStockStatus}
              />
            </div>
          </div>
          <div className={`${showFull ? "" : isHovered ? "lg:bg-white" : ""} w-full pt-[18px]`}>
            <div
              className={`mb-[18px] flex flex-nowrap justify-center transition-all duration-500
                ${showFull
                  ? ""
                  : "lg:invisible lg:opacity-0 group-hover:lg:visible group-hover:lg:opacity-100"
                }`}
            >
              <SizeSelector
                activeSize={size}
                setVariationId={setVariationId}
                sizes={variationsByColor[color]}
                setSize={setSize}
                setStockStatus={setStockStatus}
              />
            </div>
            <AddToCartButton
              productId={variationId}
              quantity={quantity}
              stockStatus={stockStatus}
              className={`w-full ${isHovered ? "" : "lg:w-0"}
                py-[14px] transition-all duration-100 delay-200 group-hover:block`}
              product={product}
              currency={currency}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

type ImageProps = {
  product?: Product;
  mainImageLoading?: "eager" | "lazy";
  currentImage?: IGatsbyImageData | string;
  altText?: string;
};

const MainImage: React.FC<ImageProps> = ({ altText, mainImageLoading, currentImage }) => {
  if (typeof currentImage === "string") {
    return (
      <img
        className="!absolute left-0 top-0 w-full h-full object-contain opacity-100 transition-all duration-500 hover:opacity-0"
        src={currentImage}
        alt={altText}
        loading={mainImageLoading || "eager"}
      />
    );
  } else {
    return (
      <GatsbyImage
        image={currentImage}
        className="!absolute left-0 top-0 block object-contain opacity-100 transition-all duration-500 hover:opacity-0"
        alt={altText}
        sizes="(max-width: 800px) 100vw, 800px"
        loading={mainImageLoading || "eager"}
      />
    );
  }
};

const SecondaryImage: React.FC<ImageProps> = ({ product }) => {
  const image =
    product.images[0]?.localFile?.childrenImageSharp[0].gatsbyImageData ||
    product.image?.localFile?.childrenImageSharp[0].gatsbyImageData;

  const imageSrc = product.images[0]?.src || product.image.src;
  const imageAlt = product.images[0]?.alt || product.image.alt;

  if (image) {
    return (
      <GatsbyImage
        image={image}
        className="!absolute left-0 top-0 block object-contain opacity-0 transition-all duration-500 hover:opacity-100"
        alt={imageAlt}
        sizes="(max-width: 800px) 100vw, 800px"
        loading="lazy"
      />
    );
  } else
    return (
      <img
        className="!absolute left-0 top-0 block w-max object-contain opacity-0 transition-all duration-500 hover:opacity-100"
        src={imageSrc}
        alt={imageAlt}
        loading="lazy"
      />
    );
};

type ColorSelectorProps = {
  className?: string;
  activeColor: string;
  colors: Record<string, ProductVariation[]>;
  setColor: React.Dispatch<React.SetStateAction<string>>;
  product: Product;
  activeSize: string;
  setVariationId: (id: number) => void;
  setStockStatus: (status: string) => void;
};

const ColorSelector: React.FC<ColorSelectorProps> = ({
  className = "",
  activeColor,
  colors,
  setColor,
  product,
  activeSize,
  setVariationId,
  setStockStatus,
}) => {
  const colorData = useProductColors();

  const handleClick = (key: string, value: ProductVariation[]) => {
    setColor(key);
    if (colors[key]) {
      let variation = colors[key].find(color => {
        return color.attributes2.find(attribute => {
          return attribute.attribute == 'pa_size' && attribute.value == activeSize;
        });
      });

      if (variation) {
        setVariationId(variation.id);
        setStockStatus(variation.stock_status);
      }
    }
  };

  return (
    <div className={`${className}`}>
      <div className="flex flex-wrap items-center gap-[16px]">
        {Object.entries(colors).slice(0, 6).map(([color, value]) => {
          const color_id = color;
          const key = value[0].attributes[0].option.replaceAll(" ", "-").toLowerCase()

          return (
            <ColorButton
              key={key}
              id={`${product.id}-${key}-${color_id}`}
              color={colorData[key]}
              isSelected={color_id === activeColor}
              onClick={() => handleClick(color_id, value)}
            />
          );
        })}
        {Object.entries(colors).length > 6 && (
          <div className="svg">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M14.0007 15.1668C14.645 15.1668 15.1673 14.6445 15.1673 14.0002C15.1673 13.3558 14.645 12.8335 14.0007 12.8335C13.3563 12.8335 12.834 13.3558 12.834 14.0002C12.834 14.6445 13.3563 15.1668 14.0007 15.1668Z" stroke="#B7B7B1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M22.1667 15.1668C22.811 15.1668 23.3333 14.6445 23.3333 14.0002C23.3333 13.3558 22.811 12.8335 22.1667 12.8335C21.5223 12.8335 21 13.3558 21 14.0002C21 14.6445 21.5223 15.1668 22.1667 15.1668Z" stroke="#B7B7B1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5.83268 15.1668C6.47701 15.1668 6.99935 14.6445 6.99935 14.0002C6.99935 13.3558 6.47701 12.8335 5.83268 12.8335C5.18835 12.8335 4.66602 13.3558 4.66602 14.0002C4.66602 14.6445 5.18835 15.1668 5.83268 15.1668Z" stroke="#B7B7B1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

type SizeSelectorProps = {
  className?: string;
  activeSize: string;
  sizes: ProductVariation[];
  setSize: (size: string) => void;
  setVariationId: (id: number) => void;
  setStockStatus: (status: string) => void;
};

const SizeSelector: React.FC<SizeSelectorProps> = ({
  className = "",
  activeSize,
  sizes,
  setSize,
  setVariationId,
  setStockStatus,
}) => {
  return (
    <div className={`${className}`}>
      <div className="flex gap-[16px]">
        <div className="flex flex-nowrap justify-center items-center">
          {!!(sizes) && Object.entries(sizes)
            .sort((a, b) => a[1].menu_order - b[1].menu_order).slice(0, 6)
            .map(([key, value]) => {
              const sizeNormalized = value.attributes2[1].value

              const handleClick = () => {
                setVariationId(value.id);
                setSize(sizeNormalized);
                setStockStatus(value.stock_status);
              };

              return (
                <SizeButton
                  key={key}
                  text={value.attributes[1].option}
                  isSelected={activeSize === sizeNormalized}
                  onClick={handleClick}
                />
              );
            })}
          {!!(sizes) && Object.entries(sizes).length > 6 && (
            <div className="svg">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none">
                <path d="M14.0007 15.1668C14.645 15.1668 15.1673 14.6445 15.1673 14.0002C15.1673 13.3558 14.645 12.8335 14.0007 12.8335C13.3563 12.8335 12.834 13.3558 12.834 14.0002C12.834 14.6445 13.3563 15.1668 14.0007 15.1668Z" stroke="#B7B7B1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22.1667 15.1668C22.811 15.1668 23.3333 14.6445 23.3333 14.0002C23.3333 13.3558 22.811 12.8335 22.1667 12.8335C21.5223 12.8335 21 13.3558 21 14.0002C21 14.6445 21.5223 15.1668 22.1667 15.1668Z" stroke="#B7B7B1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5.83268 15.1668C6.47701 15.1668 6.99935 14.6445 6.99935 14.0002C6.99935 13.3558 6.47701 12.8335 5.83268 12.8335C5.18835 12.8335 4.66602 13.3558 4.66602 14.0002C4.66602 14.6445 5.18835 15.1668 5.83268 15.1668Z" stroke="#B7B7B1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
